import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ApSimpleDropdown } from '@alixpartners/ui-components';
import { exportSpendOvertime, getSpendOverTime } from 'utils/apiRequests';
import { HeaderLabel, HeaderTop, SectionWrapper } from '../styles';
import { SpendOverTimeChart } from './SpendOverTimeChart';
import { EXPORT_OPTIONS } from '../constants';
import { CHART_COLORS } from 'styles/colors';
import { usePrevious } from '../utils';
import { createCategories, createTimeline } from './utils';
import { isEqual } from 'lodash';

const createChartSeries = data => {
	const timeline = createTimeline(data);
	const categories = createCategories(timeline);
	const series = data.map((companyData, i) => {
		return {
			color: CHART_COLORS[i],
			name: companyData.company,
			data: timeline.map(({ year, month }, index) => {
				const companyDataForTime = companyData.spendOverTime.find(
					data => data.month === month && data.year === year
				);
				const spend = companyDataForTime ? companyDataForTime.spend : 0;
				return {
					month,
					year,
					x: index,
					y: spend,
					spend,
				};
			}),
		};
	});
	return { categories, series };
};

const SpendOverTimeContainer = ({
	currencySymbol,
	queryParams,
	onDownloadStart,
	onDownloadSuccess,
	onDownloadError,
}) => {
	const { currency } = queryParams;
	const chartRef = useRef();
	const [chartData, setChartData] = useState({ categories: [], series: [] });
	let isSkipInitialSetData = false;

	const prevQueryParams = usePrevious(queryParams);
	useEffect(() => {
		getSpendOverTime(queryParams).then(result => {
			if (!isSkipInitialSetData) {
				setChartData(createChartSeries(result));
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	useEffect(
		() => {
			if (!isEqual(prevQueryParams, queryParams)) {
				// eslint-disable-next-line react-hooks/exhaustive-deps
				isSkipInitialSetData = true;
				getSpendOverTime(queryParams).then(result => {
					setChartData(createChartSeries(result));
				});
			}
		},
		[queryParams]
	);
	const getChartInstance = () => {
		return chartRef && chartRef.current && chartRef.current.chart;
	};
	const handleExport = (chartInstance, filename) => {
		if (chartInstance) {
			chartInstance.exportChart({ filename }, { chart: { width: 1200 } });
		}
	};

	const handleChartDataExport = useCallback(
		() => {
			onDownloadStart();
			exportSpendOvertime(queryParams)
				.catch(onDownloadError)
				.finally(onDownloadSuccess);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[queryParams]
	);

	return (
		<SectionWrapper>
			<HeaderTop>
				<HeaderLabel>Spend over time</HeaderLabel>
				<ApSimpleDropdown
					iconName="outline_save_alt"
					onChange={s => {
						if (s.value === 'png') {
							handleExport(getChartInstance(), 'SXR-Spend_over_time');
						} else if (s.value === 'xlsx') {
							handleChartDataExport();
						}
					}}
					options={EXPORT_OPTIONS}
					label="Export"
				/>
			</HeaderTop>
			<SpendOverTimeChart
				categories={chartData.categories}
				chartRef={chartRef}
				currency={currency}
				currencySymbol={currencySymbol}
				series={chartData.series}
			/>
		</SectionWrapper>
	);
};

export default SpendOverTimeContainer;
